import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import {
  GiftCsMemo,
  GiftCsMemoCreateDto,
  GiftCsMemoLogItem,
  GiftCsMemoLogParams,
  GiftCsMemoLogResponse,
  GiftCsMemoTarget,
  resolveCsMemoTarget,
} from './giftCsMemo.interface';

// CS 응대 메모 (Admin, GiftAgent 두 롤 모두 사용 가능 — 기존 /admin/gift-coupon과 동일)
// 구서버엔 없는 엔드포인트 — 404 가능(호출부 가드). 스펙: docs/gift-cs-memo-spec.md
const BASE = `/${LowerRole.Admin}/gift-cs-memo`;

export const isCsMemoNotFound = (e: unknown): boolean =>
  (e as { response?: { status?: number } })?.response?.status === 404;

/**
 * 대상별 타임라인 조회 (createdAt 오름차순).
 * - giftOrder 축: 그 주문의 쿠폰·배송 단위 메모까지 전부 (구매자 통합 타임라인)
 * - giftCoupon / giftDelivery 축: 해당 건만
 * - adminOnly 메모는 요청자가 Admin일 때만 포함된다 (virtual populate로는 안 실림)
 */
export const getGiftCsMemos = async (
  target: GiftCsMemoTarget,
): Promise<GiftCsMemo[]> => {
  const resolved = resolveCsMemoTarget(target);
  if (!resolved) return [];
  const params = new URLSearchParams({ [resolved.axis]: resolved.id });
  const { data } = await api_.get<GiftCsMemo[]>(`${BASE}?${params}`);
  return data;
};

/**
 * 주문 다건 배치 조회 (목록 화면용, 상한 100건 — 초과 시 서버가 400).
 * 메모 없는 주문도 빈 배열로 키가 온다 ("미조회"와 "0건" 구분).
 * 구서버엔 라우트가 없어 404 → 호출부가 주문별 개별 조회로 폴백한다.
 */
export const getGiftCsMemosBatch = async (
  orderIds: string[],
): Promise<Record<string, GiftCsMemo[]>> => {
  const params = new URLSearchParams({ giftOrders: orderIds.join(',') });
  const { data } = await api_.get<Record<string, GiftCsMemo[]>>(
    `${BASE}/batch?${params}`,
  );
  return data;
};

/**
 * CS 응대 로그 — 기간·단위·검색어로 전체 메모를 훑는 목록 (createdAt 내림차순).
 * 대상 요약(주문자·쿠폰코드·상품명)이 붙어 와서 표 한 줄로 읽힌다.
 * GiftAgent 요청이면 adminOnly 메모는 서버가 제외한다.
 */
export const getGiftCsMemoLog = async (
  params: GiftCsMemoLogParams,
): Promise<GiftCsMemoLogResponse> => {
  const query = new URLSearchParams();
  if (params.from) query.set('from', params.from);
  if (params.to) query.set('to', params.to);
  if (params.target && params.target !== 'all') query.set('target', params.target);
  if (params.q) query.set('q', params.q);
  if (params.adminOnly) query.set('adminOnly', '1');
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));

  const { data } = await api_.get<GiftCsMemoLogResponse>(`${BASE}/log?${query}`);
  return data;
};

/** 한 번에 받아오는 페이지 크기 / 최대 페이지 수 (= 최대 2000건) */
export const CS_MEMO_LOG_ALL_PAGE_SIZE = 200;
export const CS_MEMO_LOG_ALL_MAX_PAGES = 10;

/**
 * 조회 조건 전체를 페이지 넘겨가며 모아 온다 (통계 패널·엑셀 공용).
 * 상한(2000건)에 걸리면 잘린 채로 돌려주고, `capped` 로 알린다 — 화면이 "전체 집계"라고
 * 잘못 말하지 않게 하려는 것. 기간을 나눠 받으라는 안내를 띄우는 근거값이다.
 */
export const getGiftCsMemoLogAll = async (
  params: GiftCsMemoLogParams,
): Promise<{ items: GiftCsMemoLogItem[]; total: number; capped: boolean }> => {
  const items: GiftCsMemoLogItem[] = [];
  let total = 0;

  for (let page = 1; page <= CS_MEMO_LOG_ALL_MAX_PAGES; page += 1) {
    const res = await getGiftCsMemoLog({
      ...params,
      page,
      limit: CS_MEMO_LOG_ALL_PAGE_SIZE,
    });
    total = res.total;
    items.push(...res.items);
    if (items.length >= res.total || res.items.length < CS_MEMO_LOG_ALL_PAGE_SIZE) break;
  }

  return { items, total, capped: items.length < total };
};

/** 메모 작성 — 응답에 byName/byRole/createdAt이 포함돼 그대로 낙관적 갱신에 쓸 수 있다 */
export const createGiftCsMemo = async (
  dto: GiftCsMemoCreateDto,
): Promise<GiftCsMemo> => {
  const { data } = await api_.post<GiftCsMemo>(BASE, dto);
  return data;
};

/**
 * 메모 삭제 (오기입 취소용). 백엔드 미구현 상태 — 404/405면 호출부가 안내만 띄운다.
 * 요청 계약: docs/gift-cs-memo-spec.md §8
 */
export const deleteGiftCsMemo = async (id: string): Promise<void> => {
  await api_.delete(`${BASE}/${id}`);
};

/** 삭제 엔드포인트 미배포 판별 (404 = 라우트 없음, 405 = 메서드 미허용) */
export const isCsMemoDeleteUnsupported = (e: unknown): boolean => {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 404 || status === 405;
};
